import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  BuildingOffice2Icon,
  CubeTransparentIcon,
  PencilSquareIcon,
  WrenchScrewdriverIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

const services = [
  {
    icon: BuildingOffice2Icon,
    title: "Civil Consultancy",
    description:
      "Expert civil consultancy services providing innovative and sustainable solutions for infrastructure development. From planning and design to project management and execution, our team ensures every project meets the highest standards of quality and efficiency.",
    image: "https://images.unsplash.com/photo-1645597459770-4d42ef850a18?w=600&q=80",
    features: [
      "Infrastructure Planning & Design",
      "Project Management & Execution",
      "Quality Assurance & Control",
      "Cost Estimation & Budgeting",
      "Regulatory Compliance",
      "Site Supervision",
    ],
  },
  {
    icon: CubeTransparentIcon,
    title: "Structural Engineering",
    description:
      "Safety-critical structural design ensuring durability and resilience. Our structural consultancy services offer innovative and robust engineering solutions for every project, from initial design to final inspection.",
    image: "https://images.unsplash.com/photo-1631171992385-784ae02b1acb?w=600&q=80",
    features: [
      "Load Analysis & Calculations",
      "Seismic Design & Analysis",
      "Structural Audits & Assessments",
      "Retrofit & Strengthening",
      "Foundation Design",
      "Steel & RCC Design",
    ],
  },
  {
    icon: PencilSquareIcon,
    title: "Architectural Design",
    description:
      "Aesthetic functionalism blending art with utility. Our architectural consultancy offers innovative design solutions that seamlessly blend aesthetics and functionality, creating spaces that inspire and endure.",
    image: "https://images.unsplash.com/photo-1632400990400-416d5460f337?w=600&q=80",
    features: [
      "3D Visualization & Rendering",
      "Interior Design Solutions",
      "Space Planning & Optimization",
      "Facade Design",
      "Landscape Architecture",
      "Sustainable Design",
    ],
  },
  {
    icon: WrenchScrewdriverIcon,
    title: "Turnkey Construction",
    description:
      "End-to-end project management from soil to skyline. We offer turnkey construction services, managing every aspect of your project from inception to completion, delivering high-quality, ready-to-use spaces.",
    image: "https://images.unsplash.com/photo-1763951515644-bc7193af8e33?w=600&q=80",
    features: [
      "Complete Project Delivery",
      "Resource Management",
      "Timeline & Budget Control",
      "Vendor Coordination",
      "Quality Control",
      "Handover & Documentation",
    ],
  },
];

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-zinc-50"
    >
      <Navbar />
      <main className="pt-20">
        {/* Page Header */}
        <section className="py-20 bg-slate-900 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                Our Services
              </span>
              <h1 className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4">
                Comprehensive <span className="text-orange-500">Solutions</span> for Every Project
              </h1>
              <p className="text-slate-300 mt-6 text-lg font-manrope max-w-3xl mx-auto">
                From concept to completion, we provide integrated services that
                cover every aspect of your construction and design needs.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services List */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid md:grid-cols-2 gap-12 items-center py-16 ${
                  index !== services.length - 1 ? "border-b border-slate-200" : ""
                }`}
                data-testid={`service-${index}`}
              >
                <div className={`${index % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[350px] object-cover"
                    />
                    <div className="absolute top-4 left-4 w-14 h-14 bg-orange-500 flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className={`${index % 2 === 1 ? "md:order-1" : ""}`}>
                  <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-slate-600 font-manrope leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-slate-700 text-sm font-manrope">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-orange-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-white mb-6">
              Need Our Services?
            </h2>
            <p className="text-white/90 font-manrope mb-8">
              Get in touch with us today for a free consultation and project estimate.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-slate-900 text-white px-8 py-4 font-manrope font-semibold uppercase tracking-wider text-sm hover:bg-slate-800 transition-colors"
              data-testid="services-cta"
            >
              Request Quote
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
};

export default ServicesPage;
